/**
 * useFlow Hook
 *
 * A convenience hook for interacting with the flow modal system.
 * Provides a clean, simple API for starting flows from anywhere in the app.
 *
 * USAGE:
 * ```tsx
 * const { startPrivateSaleVendor, isOpen } = useFlow();
 *
 * <Button onClick={() => startPrivateSaleVendor()}>View Offer</Button>
 * ```
 */
import { useCallback, useMemo } from "react";

import { getFlowProgress, getOrderedSteps } from "@/app/_components/flow-modal/flow-config";
import type { FlowData, FlowType } from "@/app/_components/flow-modal/flow-types";
import {
  selectCurrentStep,
  selectModalState,
  selectNavigation,
  useFlowStore,
} from "@/stores/flow-store";

/**
 * Main hook for interacting with the flow system.
 */
export function useFlow() {
  const store = useFlowStore();

  // ============================================================================
  // Flow Starters (typed convenience methods)
  // ============================================================================

  const startPrivateSaleVendor = useCallback(
    (initialData?: FlowData, initialStepId?: string) => {
      store.startFlow("PRIVATE_SALE_VENDOR", initialStepId, initialData);
    },
    [store],
  );

  const startPrivateSaleBuyer = useCallback(
    (initialData?: FlowData, initialStepId?: string) => {
      store.startFlow("PRIVATE_SALE_BUYER", initialStepId, initialData);
    },
    [store],
  );

  const startLiveAuctionBuyer = useCallback(
    (initialData?: FlowData, initialStepId?: string) => {
      store.startFlow("LIVE_AUCTION_BUYER", initialStepId, initialData);
    },
    [store],
  );

  const startLiveAuctionVendor = useCallback(
    (initialData?: FlowData, initialStepId?: string) => {
      store.startFlow("LIVE_AUCTION_VENDOR", initialStepId, initialData);
    },
    [store],
  );

  const startReverseAuctionBuyer = useCallback(
    (initialData?: FlowData, initialStepId?: string) => {
      store.startFlow("REVERSE_AUCTION_BUYER", initialStepId, initialData);
    },
    [store],
  );

  const startReverseAuctionVendor = useCallback(
    (initialData?: FlowData, initialStepId?: string) => {
      store.startFlow("REVERSE_AUCTION_VENDOR", initialStepId, initialData);
    },
    [store],
  );

  // Generic starter for dynamic flow type
  const startFlow = useCallback(
    (flowType: FlowType, initialData?: FlowData, initialStepId?: string) => {
      store.startFlow(flowType, initialStepId, initialData);
    },
    [store],
  );

  // ============================================================================
  // Flow Controls
  // ============================================================================

  const closeFlow = useCallback(
    (reset = false) => {
      store.closeFlow(reset);
    },
    [store],
  );

  const resetFlow = useCallback(() => {
    store.resetFlow();
  }, [store]);

  // ============================================================================
  // State Accessors
  // ============================================================================

  const progress = useMemo(() => {
    if (!store.flowType || !store.currentStepId) return 0;
    return getFlowProgress(store.flowType, store.currentStepId);
  }, [store.flowType, store.currentStepId]);

  const totalSteps = useMemo(() => {
    if (!store.flowType) return 0;
    return getOrderedSteps(store.flowType).length;
  }, [store.flowType]);

  const currentStepIndex = useMemo(() => {
    if (!store.flowType || !store.currentStepId) return 0;
    const steps = getOrderedSteps(store.flowType);
    return steps.findIndex((s) => s.id === store.currentStepId) + 1;
  }, [store.flowType, store.currentStepId]);

  return {
    // Flow starters
    startPrivateSaleVendor,
    startPrivateSaleBuyer,
    startLiveAuctionBuyer,
    startLiveAuctionVendor,
    startReverseAuctionBuyer,
    startReverseAuctionVendor,
    startFlow,

    // Flow controls
    closeFlow,
    resetFlow,

    // State
    isOpen: store.isOpen,
    isLoading: store.isLoading,
    error: store.error,
    flowType: store.flowType,
    currentStepId: store.currentStepId,
    flowData: store.flowData,

    // Progress info
    progress,
    totalSteps,
    currentStepIndex,
    canGoBack: store.history.length > 1,

    // Direct store access for advanced usage
    store,
  };
}

/**
 * Hook for components that only need to read modal state (no actions).
 * Uses selectors for optimized re-renders.
 */
export function useFlowModalState() {
  return useFlowStore(selectModalState);
}

/**
 * Hook for components that need current step info.
 */
export function useFlowCurrentStep() {
  return useFlowStore(selectCurrentStep);
}

/**
 * Hook for components that need navigation state.
 */
export function useFlowNavigation() {
  return useFlowStore(selectNavigation);
}
